import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
  BusinessProductDto,
  BusinessProductRequest,
  BusinessStockDto,
  ExportCheckpointDto,
  SaleOfferAcceptRequest,
  SaleOfferDto,
  SaleOfferRequest,
  SaleOfferStatus,
  TradeBusinessOptionDto,
  TradeChainDto,
  TradeHarvestRequest,
  TradeObservationDto,
  TradeRecordDto,
  TradeRecordKind,
  TradeRecordLogDto,
  TradeRecordUpdateRequest,
  TradeTransformRequest
} from '../../../models/generated-models';

export interface TradeRecordListParams {
  businessId?: number | null;
  kind?: TradeRecordKind | null;
  search?: string | null;
}

export interface SaleOfferListParams {
  businessId?: number | null;
  status?: SaleOfferStatus | null;
}

/** One method per endpoint of `TradeRecordResource` (`/api/trade-records`). */
@Injectable({providedIn: 'root'})
export class TradeApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.serverUrl}/api/trade-records`;

  /** The officer report (ข้อสังเกต); the backend answers 403 for anyone but staff. */
  listObservations(): Observable<TradeObservationDto[]> {
    return this.http.get<TradeObservationDto[]>(`${this.baseUrl}/observations`);
  }

  listMyBusinesses(): Observable<TradeBusinessOptionDto[]> {
    return this.http.get<TradeBusinessOptionDto[]>(`${this.baseUrl}/my-businesses`);
  }

  listStock(businessId?: number | null): Observable<BusinessStockDto[]> {
    const params = businessId == null ? undefined : new HttpParams().set('businessId', businessId);
    return this.http.get<BusinessStockDto[]>(`${this.baseUrl}/stock`, {params});
  }

  listRecords(filter: TradeRecordListParams = {}): Observable<TradeRecordDto[]> {
    let params = new HttpParams();
    if (filter.businessId != null) {
      params = params.set('businessId', filter.businessId);
    }
    if (filter.kind) {
      params = params.set('kind', filter.kind);
    }
    const search = filter.search?.trim();
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<TradeRecordDto[]>(this.baseUrl, {params});
  }

  getRecord(id: number): Observable<TradeRecordDto> {
    return this.http.get<TradeRecordDto>(`${this.baseUrl}/${id}`);
  }

  /** Audit trail of one record, newest first (404 when the record is not visible). */
  getHistory(id: number): Observable<TradeRecordLogDto[]> {
    return this.http.get<TradeRecordLogDto[]>(`${this.baseUrl}/${id}/history`);
  }

  getChain(id: number): Observable<TradeChainDto> {
    return this.http.get<TradeChainDto>(`${this.baseUrl}/${id}/chain`);
  }

  lookup(code: string): Observable<TradeRecordDto> {
    return this.http.get<TradeRecordDto>(`${this.baseUrl}/lookup`, {
      params: new HttpParams().set('code', code.trim())
    });
  }

  listUnits(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/units`);
  }

  listProductNames(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/product-names`);
  }

  /** ด่านส่งออก master — an exporter picks the checkpoint its record ends at. */
  listExportCheckpoints(): Observable<ExportCheckpointDto[]> {
    return this.http.get<ExportCheckpointDto[]>(`${this.baseUrl}/export-checkpoints`);
  }

  createHarvest(request: TradeHarvestRequest): Observable<TradeRecordDto> {
    return this.http.post<TradeRecordDto>(this.baseUrl, request);
  }

  // --- business products (สินค้าที่ธุรกิจเคยบันทึก) --------------------------
  // A product remembers the certificates it was sold under, so the next
  // harvest of the same product is prefilled.

  listProducts(businessId?: number | null): Observable<BusinessProductDto[]> {
    const params = businessId == null ? undefined : new HttpParams().set('businessId', businessId);
    return this.http.get<BusinessProductDto[]>(`${this.baseUrl}/products`, {params});
  }

  /** Upsert by business + product name; replaces the certificate list. */
  upsertProduct(request: BusinessProductRequest): Observable<BusinessProductDto> {
    return this.http.put<BusinessProductDto>(`${this.baseUrl}/products`, request);
  }

  deleteProduct(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/products/${id}`);
  }

  // --- sale offers (ใบขาย) ------------------------------------------------
  // The seller commits a quantity up front; the buyer only accepts it.

  createOffer(request: SaleOfferRequest): Observable<SaleOfferDto> {
    return this.http.post<SaleOfferDto>(`${this.baseUrl}/offers`, request);
  }

  listOffers(filter: SaleOfferListParams = {}): Observable<SaleOfferDto[]> {
    let params = new HttpParams();
    if (filter.businessId != null) {
      params = params.set('businessId', filter.businessId);
    }
    if (filter.status) {
      params = params.set('status', filter.status);
    }
    return this.http.get<SaleOfferDto[]>(`${this.baseUrl}/offers`, {params});
  }

  getOffer(id: number): Observable<SaleOfferDto> {
    return this.http.get<SaleOfferDto>(`${this.baseUrl}/offers/${id}`);
  }

  /** The seller's OPEN offers behind a scanned lot / stock QR (work item 169). */
  listOffersForCode(code: string): Observable<SaleOfferDto[]> {
    return this.http.get<SaleOfferDto[]>(`${this.baseUrl}/offers/for-code`, {params: {code}});
  }

  /** OPEN offers only — anything else answers 404. */
  lookupOffer(code: string): Observable<SaleOfferDto> {
    return this.http.get<SaleOfferDto>(`${this.baseUrl}/offers/lookup`, {
      params: new HttpParams().set('code', code.trim())
    });
  }

  /**
   * Accepts the whole offer: one PURCHASE record per line. An exporter business
   * ends the chain, so its records carry the export attributes. Answers the
   * accepted offer.
   */
  acceptOffer(id: number, request: SaleOfferAcceptRequest): Observable<SaleOfferDto> {
    return this.http.post<SaleOfferDto>(`${this.baseUrl}/offers/${id}/accept`, request);
  }

  cancelOffer(id: number): Observable<SaleOfferDto> {
    return this.http.post<SaleOfferDto>(`${this.baseUrl}/offers/${id}/cancel`, {});
  }

  /** Processes own stock into a new product lot (kind TRANSFORM). */
  transform(request: TradeTransformRequest): Observable<TradeRecordDto> {
    return this.http.post<TradeRecordDto>(`${this.baseUrl}/transform`, request);
  }

  updateRecord(id: number, request: TradeRecordUpdateRequest): Observable<TradeRecordDto> {
    return this.http.put<TradeRecordDto>(`${this.baseUrl}/${id}`, request);
  }

  uploadCoverImage(id: number, file: File): Observable<TradeRecordDto> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<TradeRecordDto>(`${this.baseUrl}/${id}/cover-image`, formData);
  }

  deleteRecord(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }
}
